<template>
  <div class="row mt-4" v-if="kpis">
    <div class="col-12">
      <div class="card ficha-listado">
        <div class="px-4 pb-4 card-body">
          <div class="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-3">
            <h6 class="mb-0 panel-section-title">KPIs del tipster</h6>
          </div>
          <div class="row g-3">

            <!-- Ingresos reales: HERO card, primera posición -->
            <div class="col-12 col-sm-6 col-xl-3" @click="$emit('open-ingresos-detalle')" style="cursor:pointer">
              <div class="mini-card mini-card--hero mini-card--clickable">
                <div class="hero-icon">💰</div>
                <div class="hero-label">Ingresos reales</div>
                <div class="hero-amounts">
                  <span class="hero-eur">{{ formatMoney(kpis.ingresos_totales ?? 0) }}</span>
                  <span v-if="usdReal > 0" class="hero-usd">{{ formatMoneyUsd(usdReal) }}</span>
                </div>
                <div class="mini-card-hint">Ver detalle</div>
              </div>
            </div>

            <!-- Ventas completadas -->
            <div class="col-6 col-lg-3 col-xl-2" @click="$emit('open-ventas-detalle')" style="cursor:pointer">
              <div class="mini-card mini-card--clickable">
                <div class="k">Ventas completadas</div>
                <div class="v">{{ kpis.ventas_totales ?? 0 }}</div>
                <div v-if="ventasEur > 0 || ventasUsd > 0" class="v-moneda-split">
                  <span v-if="ventasEur > 0" class="v-mon v-mon--eur">{{ ventasEur }} en €</span>
                  <span v-if="ventasUsd > 0" class="v-mon v-mon--usd">{{ ventasUsd }} en $</span>
                </div>
                <div class="mini-card-hint">Ver detalle</div>
              </div>
            </div>

            <!-- Renovaciones activas -->
            <div v-if="tieneRenovaciones" class="col-6 col-lg-3 col-xl-2" @click="$emit('open-renovaciones-detalle')" style="cursor:pointer">
              <div class="mini-card mini-card--pendiente mini-card--clickable">
                <div class="k">Renovaciones activas</div>
                <div class="v-multi">
                  <span v-if="(kpis.importe_pendiente ?? 0) > 0" class="v-cur v-cur--eur">{{ formatMoney(kpis.importe_pendiente) }}</span>
                  <span v-if="(kpis.importe_pendiente_usd ?? 0) > 0" class="v-cur v-cur--usd">{{ formatMoneyUsd(kpis.importe_pendiente_usd) }}</span>
                </div>
                <div class="text-xs opacity-7">{{ kpis.trials_pendientes ?? 0 }} suscriptor{{ (kpis.trials_pendientes ?? 0) !== 1 ? 'es' : '' }} activo{{ (kpis.trials_pendientes ?? 0) !== 1 ? 's' : '' }}</div>
                <div class="mini-card-hint">Ver detalle</div>
              </div>
            </div>

            <!-- Ingresos compra única -->
            <div v-if="tieneUnicas" class="col-6 col-lg-3 col-xl-2">
              <div class="mini-card mini-card--unicas">
                <div class="k">Compra única</div>
                <div class="v-multi">
                  <span v-if="(kpis.ingresos_unicas_eur ?? 0) > 0" class="v-cur v-cur--eur">{{ formatMoney(kpis.ingresos_unicas_eur) }}</span>
                  <span v-if="(kpis.ingresos_unicas_usd ?? 0) > 0" class="v-cur v-cur--usd">{{ formatMoneyUsd(kpis.ingresos_unicas_usd) }}</span>
                </div>
              </div>
            </div>

            <!-- Ingresos suscripción -->
            <div v-if="tieneSuscs" class="col-6 col-lg-3 col-xl-2">
              <div class="mini-card mini-card--suscs">
                <div class="k">Suscripciones</div>
                <div class="v-multi">
                  <span v-if="(kpis.ingresos_suscs_eur ?? 0) > 0" class="v-cur v-cur--eur">{{ formatMoney(kpis.ingresos_suscs_eur) }}</span>
                  <span v-if="(kpis.ingresos_suscs_usd ?? 0) > 0" class="v-cur v-cur--usd">{{ formatMoneyUsd(kpis.ingresos_suscs_usd) }}</span>
                </div>
              </div>
            </div>

            <!-- Ticket medio: EUR y USD separados -->
            <div class="col-6 col-lg-3 col-xl-2">
              <div class="mini-card">
                <div class="k">Ticket medio</div>
                <div class="v-multi">
                  <span class="v-cur v-cur--eur">{{ formatMoney(kpis.ticket_medio_eur ?? kpis.ticket_medio ?? 0) }}</span>
                  <span v-if="(kpis.ticket_medio_usd ?? 0) > 0" class="v-cur v-cur--usd">{{ formatMoneyUsd(kpis.ticket_medio_usd) }}</span>
                </div>
              </div>
            </div>

            <!-- Devoluciones -->
            <div v-if="tieneDevoluciones" class="col-6 col-lg-3 col-xl-2" @click="$emit('open-devoluciones-detalle')" style="cursor:pointer">
              <div class="mini-card mini-card--devolucion mini-card--clickable">
                <div class="mini-card-icon-dev">↩</div>
                <div class="k">Devoluciones</div>
                <div class="v-multi">
                  <span v-if="devImporteEur > 0" class="v-cur v-cur--dev-eur">
                    -{{ formatMoney(devImporteEur) }}
                  </span>
                  <span v-if="devImporteUsd > 0" class="v-cur v-cur--dev-usd">
                    -{{ formatMoneyUsd(devImporteUsd) }}
                  </span>
                </div>
                <div class="text-xs dev-count mt-1">
                  <template v-if="devCountEur > 0">{{ devCountEur }} en €</template>
                  <template v-if="devCountEur > 0 && devCountUsd > 0"> · </template>
                  <template v-if="devCountUsd > 0">{{ devCountUsd }} en $</template>
                </div>
                <div class="mini-card-hint">Ver detalle</div>
              </div>
            </div>

            <!-- Top ventas -->
            <div class="col-6 col-lg-3 col-xl-2" @click="$emit('open-top-ventas-detalle')" style="cursor:pointer">
              <div class="mini-card mini-card--gold mini-card--clickable">
                <div class="mini-card-trophy">🏆</div>
                <div class="k">Producto más vendido</div>
                <div class="v text-sm">{{ kpis.producto_top_ventas?.producto || "-" }}</div>
                <div class="text-xs mini-card-sub">{{ kpis.producto_top_ventas?.ventas ?? 0 }} ventas</div>
                <div class="mini-card-hint">Ver compradores</div>
              </div>
            </div>

            <!-- Top ingresos -->
            <div class="col-12 col-lg-3 col-xl-2" @click="$emit('open-top-ingresos-detalle')" style="cursor:pointer">
              <div class="mini-card mini-card--gold mini-card--clickable">
                <div class="mini-card-trophy">💰</div>
                <div class="k">Producto más rentable</div>
                <div class="v text-sm">{{ kpis.producto_top_ingresos?.producto || "-" }}</div>
                <div class="text-xs mini-card-sub">{{ formatMoney(kpis.producto_top_ingresos?.ingresos ?? 0) }}</div>
                <div class="mini-card-hint">Ver compradores</div>
              </div>
            </div>

          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "PanelTipsterKpis",
  props: {
    kpis: { type: Object, default: null },
  },
  emits: ["open-ventas-detalle", "open-ingresos-detalle", "open-renovaciones-detalle", "open-top-ventas-detalle", "open-top-ingresos-detalle", "open-devoluciones-detalle"],
  computed: {
    ventasEur() { return this.kpis?.ventas_por_moneda?.EUR ?? 0; },
    ventasUsd() { return this.kpis?.ventas_por_moneda?.USD ?? 0; },
    usdReal() {
      return this.kpis?.ingresos_por_moneda?.USD ?? 0;
    },
    usdProyectado() {
      return (this.kpis?.ingresos_por_moneda?.USD ?? 0) + (this.kpis?.importe_pendiente_usd ?? 0);
    },
    tieneRenovaciones() {
      return (this.kpis?.importe_pendiente ?? 0) > 0 || (this.kpis?.importe_pendiente_usd ?? 0) > 0;
    },
    tieneUnicas() {
      return (this.kpis?.ingresos_unicas_eur ?? 0) > 0 || (this.kpis?.ingresos_unicas_usd ?? 0) > 0;
    },
    tieneSuscs() {
      return (this.kpis?.ingresos_suscs_eur ?? 0) > 0 || (this.kpis?.ingresos_suscs_usd ?? 0) > 0;
    },
    devImporteEur() { return this.kpis?.devoluciones_importe_eur ?? 0; },
    devImporteUsd() { return this.kpis?.devoluciones_importe_usd ?? 0; },
    devCountEur()   { return this.kpis?.devoluciones_count_eur ?? 0; },
    devCountUsd()   { return this.kpis?.devoluciones_count_usd ?? 0; },
    tieneDevoluciones() { return this.devImporteEur > 0 || this.devImporteUsd > 0; },
  },
  methods: {
    formatMoney(n) {
      return Number(n || 0).toLocaleString("es-ES", { style: "currency", currency: "EUR" });
    },
    formatMoneyUsd(n) {
      return Number(n || 0).toLocaleString("en-US", { style: "currency", currency: "USD" });
    },
  },
};
</script>

<style scoped>
.mini-card {
  border: 1px solid var(--q-border);
  background: var(--q-card);
  border-radius: 12px;
  padding: 12px 14px;
  height: 100%;
  min-height: 92px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;
  gap: 6px;
}
.mini-card .k {
  font-size: 12px;
  opacity: .7;
  line-height: 1.15;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.mini-card .v { font-size: 18px; font-weight: 700; }

/* Desglose ventas por moneda */
.v-moneda-split {
  display: flex;
  gap: 8px;
  justify-content: center;
  flex-wrap: wrap;
}
.v-mon {
  font-size: 11px;
  font-weight: 600;
  padding: 1px 7px;
  border-radius: 20px;
}
.v-mon--eur { background: rgba(212,175,55,0.15); color: var(--q-gold); }
.v-mon--usd { background: rgba(38,198,218,0.15); color: #26c6da; }

/* Contenedor multi-moneda: EUR y USD en fila, mismo tamaño */
.v-multi {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  justify-content: center;
  gap: 6px 10px;
}
.v-cur {
  font-size: 17px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}
.v-cur--eur { color: var(--q-gold); }
.v-cur--usd { color: #26c6da; }


/* .mini-card--pendiente colores definidos en sección Hero card */
.mini-card--unicas {
  border-color: rgba(212, 175, 55, 0.3);
  background: rgba(212, 175, 55, 0.05);
}
.mini-card--suscs {
  border-color: rgba(38, 198, 218, 0.3);
  background: rgba(38, 198, 218, 0.05);
}
.mini-card--gold {
  border-color: rgba(212, 175, 55, 0.5);
  background: linear-gradient(145deg, rgba(212, 175, 55, 0.08), rgba(212, 175, 55, 0.03));
  position: relative;
  overflow: hidden;
}
.mini-card--gold::before {
  content: '';
  position: absolute;
  inset: 0;
  background: linear-gradient(135deg, rgba(212,175,55,0.12) 0%, transparent 60%);
  pointer-events: none;
}
.mini-card--gold .k { color: #b8962e; font-weight: 700; }
.mini-card--gold .v { color: #d4af37; }
.mini-card--devolucion {
  border-color: rgba(239, 83, 80, 0.35);
  background: rgba(239, 83, 80, 0.07);
}
.mini-card--devolucion .k { color: #ef9a9a; }
.v-cur--dev-eur { color: #ef5350; }
.v-cur--dev-usd { color: #e57373; }
.dev-count { color: #ef9a9a; opacity: .8; }
.mini-card-icon-dev {
  font-size: 18px;
  line-height: 1;
  color: #ef5350;
  margin-bottom: 2px;
}
.mini-card-trophy {
  font-size: 20px;
  line-height: 1;
  margin-bottom: 2px;
  filter: drop-shadow(0 1px 3px rgba(212,175,55,0.4));
}
.mini-card-sub {
  opacity: .75;
  color: #b8962e;
}
/* Hero card — Ingresos reales (dorado) */
.mini-card--hero {
  border-color: rgba(212, 175, 55, 0.5);
  background: linear-gradient(145deg, rgba(212, 175, 55, 0.10), rgba(212, 175, 55, 0.04));
  min-height: 110px;
  position: relative;
}
.mini-card--hero::before {
  content: '';
  position: absolute;
  inset: 0;
  border-radius: 12px;
  background: linear-gradient(135deg, rgba(212,175,55,0.14) 0%, transparent 55%);
  pointer-events: none;
}
.mini-card--hero:hover {
  border-color: rgba(212, 175, 55, 0.75);
  background: linear-gradient(145deg, rgba(212, 175, 55, 0.15), rgba(212, 175, 55, 0.06));
}
.hero-icon {
  font-size: 22px;
  line-height: 1;
  filter: drop-shadow(0 1px 4px rgba(212,175,55,0.5));
  margin-bottom: 2px;
}
.hero-label {
  font-size: 12px;
  font-weight: 700;
  letter-spacing: .04em;
  text-transform: uppercase;
  color: rgba(212, 175, 55, 0.9);
}
.hero-amounts {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  justify-content: center;
  gap: 4px 12px;
}
.hero-eur {
  font-size: 26px;
  font-weight: 800;
  font-variant-numeric: tabular-nums;
  color: #d4af37;
  line-height: 1.1;
  white-space: nowrap;
}
.hero-usd {
  font-size: 18px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  color: #26c6da;
  white-space: nowrap;
}

/* Renovaciones activas — verde */
.mini-card--pendiente {
  border-color: rgba(72, 199, 142, 0.4);
  background: rgba(72, 199, 142, 0.07);
}
.mini-card--pendiente .v-cur--eur { color: #48c78e; }
.mini-card--pendiente .v-cur--usd { color: #80deea; }

.mini-card--clickable:hover {
  border-color: rgba(212,175,55,.5);
  background: rgba(212,175,55,.06);
}
.mini-card-hint {
  font-size: 10px;
  opacity: 0;
  color: var(--q-gold, #d4af37);
  transition: opacity .15s;
  margin-top: 2px;
}
.mini-card--clickable:hover .mini-card-hint { opacity: .7; }
</style>
